// @ts-nocheck
import * as turf from '@turf/turf';
import React from 'react';
import { useIntl } from 'react-intl';
import { useMap } from 'react-map-gl/mapbox';
import { useDispatch, useSelector } from 'react-redux';
import { useReportingMap } from 'sonata-platform-components';
import { EmptyState, useDidUpdate, usePrevious } from 'sonata-react-components';

import { centerMap } from '@/components/Map/map.helper';
import { getCartographyFilter } from '@/helpers/reporting.helpers';
import { ReportingInsightsRankingItem } from '@/modules/reporting/ReportingInsights';
import { formatFilters } from '@/modules/reporting/ReportingInsights/reporting.helpers';
import { reportingInsightsSetStats } from '@/store/actions/reporting.actions';
import { getBaseCurrency, getBaseCurrencyName } from '@/store/selectors/baseCurrency.selectors';
import { getInsightFiltered } from '@/store/selectors/reporting.selectors';
import {
  getCampaigns,
  getCartographyCountries,
  getCartographyLevel,
  getCartographyLevelIds,
  getCurrency,
  getTimeFrame,
  getTimeZone
} from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getCountry, getUserId } from '@/store/selectors/user.selectors';

type Props = {
  filterSection: string;
  isFootfallAttrMap?: boolean;
  mapId: string;
};

export function MapRankingPanel({ filterSection = 'maps', isFootfallAttrMap = false, mapId }: Props) {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const cIds = useSelector(getCampaigns);
  // @ts-ignore
  const { beginDate, endDate } = useSelector(store => getTimeFrame(store, filterSection));
  // @ts-ignore
  const timezone = useSelector(store => getTimeZone(store, filterSection));
  const baseCurrency = useSelector(getBaseCurrencyName);
  // @ts-ignore
  const currency = useSelector(store => getCurrency(store, filterSection));
  // @ts-ignore
  const currencyInfo = useSelector(store => getBaseCurrency(store, currency));
  const userId = useSelector(getUserId);
  // @ts-ignore
  const generalCartographyCountries = useSelector(store => getCartographyCountries(store, 'general'));
  // @ts-ignore
  const generalCartographyLevel = useSelector(store => getCartographyLevel(store, 'general'));
  // @ts-ignore
  const generalCartographyLevelIds = useSelector(store => getCartographyLevelIds(store, 'general'));
  // @ts-ignore
  const cartographyCountries = useSelector(store => getCartographyCountries(store, filterSection, mapId));
  // @ts-ignore
  const cartographyLevel = useSelector(store => getCartographyLevel(store, filterSection, mapId));
  // @ts-ignore
  const cartographyLevelIds = useSelector(store => getCartographyLevelIds(store, filterSection, mapId));
  const country = useSelector(getCountry);
  const insightsConfig = useSelector(store =>
    getInsightFiltered(
      store,
      // @ts-ignore
      isFootfallAttrMap ? ['faMapRanking'] : ['mapRanking']
    )
  );
  const { [mapId]: mapBoxGlMap } = useMap();
  const { getMap, getMapCartographyData, getMapEvents, getMapLocationsData, getMapScreensData, getMapStoresData } =
    useReportingMap();

  const map = getMap({ id: mapId });
  const type = map?.type;
  const event = getMapEvents({ id: mapId })[0];
  const level = map?.settings?.cartography?.level || '';
  const mapCountries = map?.settings?.cartography?.countries || '';

  const { field: insightsField, filters, groupsOptions, id, ...restConf } = insightsConfig[0];

  const getGroupDimension = () => {
    if (map?.type === 'stores') {
      return 'attrVids';
    }
    if (map?.type === 'locations') {
      return 'lid';
    }
    if (map?.type === 'screens') {
      return 'screenId';
    }
    return `cartoPyramid.${level}`;
  };

  const cartographyFilter = getCartographyFilter(
    {
      cartographyCountries: generalCartographyCountries,
      cartographyLevel: generalCartographyLevel,
      cartographyLevelIds: generalCartographyLevelIds
    },
    {
      cartographyCountries: mapCountries && mapCountries.length ? mapCountries : cartographyCountries,
      cartographyLevel,
      cartographyLevelIds
    }
  );

  const { campaignInfo, field, formattedGroupOption, metricsKpis, rankingId } = {
    campaignInfo: {
      cIds,
      cartographyFilter,
      currencyOptions: {
        baseCurrency,
        currency
      },
      dateInterval: {
        beginDate,
        endDate,
        timezone
      },
      userId
    },
    field: getGroupDimension(),
    formattedGroupOption: {
      ...groupsOptions[0],
      groupBy: getGroupDimension()
    },
    metricsKpis: {
      metric: { id: event }
    },
    rankingId: `reporting_map_${mapId}_ranking`
  };

  const prevEvent = usePrevious(event);
  const prevType = usePrevious(map?.type);
  const prevLevel = usePrevious(level);
  useDidUpdate(() => {
    if (
      !['single', 'heatmap'].includes(map?.type || 'N/A') &&
      (event !== prevEvent || map?.type !== prevType || level !== prevLevel)
    ) {
      dispatch(reportingInsightsSetStats(rankingId, undefined));
    }
  }, [event, map?.type, level]);

  const getBoundingBox = (data: Record<string, any>) => {
    let boundingBox: number[] = [];
    switch (type) {
      case 'cartography': {
        const dataId = data.cartography.name;
        const currentMapData = getMapCartographyData({ id: mapId });
        const storedItem = currentMapData.find(item => item?._id === dataId);
        if (storedItem?.boundingBox) {
          ({ boundingBox } = storedItem);
        }
        break;
      }
      case 'stores':
      case 'screens': {
        const dataId = data[`${field}Id`];
        const currentMapData = type === 'stores' ? getMapStoresData({ id: mapId }) : getMapScreensData({ id: mapId });
        const storedItem = currentMapData.find(item => item?._id === dataId);
        if (storedItem) {
          const point = turf.point([storedItem.longitude, storedItem.latitude]);
          boundingBox = turf.bbox(point);
        }
        break;
      }
      case 'locations': {
        const dataId = data[`${field}Id`];
        const currentMapData = getMapLocationsData({ id: mapId });
        const storedItem = currentMapData.find(item => item?._id === dataId);
        if (storedItem) {
          boundingBox = turf.bbox(storedItem.polygon as turf.Polygon);
        }
        break;
      }
      default:
        throw new Error(`Type: ${type}, is not a correct map type`);
    }

    return boundingBox;
  };

  const handleRankingItemClick = ({ data }: { data: Record<string, any> }) => {
    const boundingBox = getBoundingBox(data);
    // @ts-expect-error
    boundingBox?.length && centerMap(mapBoxGlMap, boundingBox);
  };

  if (['single', 'heatmap'].includes(map?.type || 'N/A')) {
    return (
      <EmptyState
        description={formatMessage({
          defaultMessage: 'The ranking is only available for the Cartography map',
          id: 'reporting.map.mapRankingPanel.description'
        })}
        show
        title={formatMessage({
          defaultMessage: 'This map view does not contain rankings by cartography',
          id: 'reporting.map.mapRankingPanel.title'
        })}
      />
    );
  }

  if (map?.type === 'cartography' && (field === 'cartoPyramid.' || !metricsKpis.metric.id)) {
    return null;
  }

  const isKpi = ['ctr', 'vtr', 'atr', 'view_inview_rate'].includes(event);
  const isFootfallVisitMetric = [
    'cpfootfall_attribution',
    'footfall_attribution_rate',
    'fa_venue_cost_per_visit',
    'fa_venue_uplift_pct'
  ].includes(event);
  const rawDataModeActive =
    (!isFootfallAttrMap && isKpi) || (isFootfallAttrMap && isFootfallVisitMetric) ? true : undefined;
  const rawDataModeDefaultActive = !isFootfallAttrMap || (isFootfallAttrMap && isFootfallVisitMetric);

  return ['single', 'heatmap'].includes(map?.type || 'N/A') ? (
    <EmptyState
      description={formatMessage({
        defaultMessage: 'The ranking is only available for the Cartography map',
        id: 'reporting.map.mapRankingPanel.description'
      })}
      fontSizes={{
        description: '12px',
        icon: '18px',
        title: '14px'
      }}
      show
      spacing="small"
      title={formatMessage({
        defaultMessage: 'This map view does not contain rankings by cartography',
        id: 'reporting.map.mapRankingPanel.title'
      })}
    />
  ) : (
    <ReportingInsightsRankingItem
      campaignInfo={campaignInfo}
      country={country}
      currencyInfo={currencyInfo}
      field={field}
      filters={formatFilters(metricsKpis, mapCountries, filters || [], true)}
      groupsOptions={[formattedGroupOption]}
      id={rankingId}
      itemId={rankingId}
      mapId={mapId}
      metricsKpis={metricsKpis}
      onRankingItemClick={handleRankingItemClick}
      rawDataMode={{
        defaultActive: rawDataModeDefaultActive,
        disabled: isFootfallVisitMetric || isKpi,
        force: rawDataModeActive
      }}
      reportOptions={{
        includeBoundingBox: true
      }}
      section={filterSection}
      {...restConf}
    />
  );
}
